import {
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import {FormControl, ReactiveFormsModule} from '@angular/forms';
import {Subject} from 'rxjs';
import {debounceTime, map, startWith, takeUntil} from 'rxjs/operators';
import {MatOptgroup, MatSelectChange, MatSelectModule, MatSelectTrigger} from "@angular/material/select";
import Fuse from "fuse.js";
import {deepCopy} from "../../lib/utils";
import {COMMON_FORM_IMPORTS} from "../../shared/import-groups/Common";
import {DblClickCopyDirective} from "../../directives/dbl-click-copy.directive";
import {NameAndDescriptionPipe} from "../../shared/pipes";
import {ProcessMenuDirective} from "../../directives/process-menu.directive";
import {CdkFixedSizeVirtualScroll, CdkVirtualForOf, CdkVirtualScrollViewport} from "@angular/cdk/scrolling";
import {CommonModule} from "@angular/common";

@Component({
    selector: 'select-search',
    templateUrl: 'select-search.component.html',
    styleUrls: ['select-search.component.less'],
    encapsulation: ViewEncapsulation.None,
    imports: [
        COMMON_FORM_IMPORTS,
        CommonModule,
        ReactiveFormsModule,
        MatSelectTrigger,
        DblClickCopyDirective,
        NameAndDescriptionPipe,
        ProcessMenuDirective,
        MatOptgroup,
        CdkVirtualForOf,
        CdkVirtualScrollViewport,
        CdkFixedSizeVirtualScroll
    ],
})
export class SelectSearchComponent implements OnInit, OnDestroy {
    filteredOptions: any[] = [];

    @ViewChild('search') searchTextBox: ElementRef;
    selectFormControl = new FormControl();
    searchTextboxControl = new FormControl();

    @Input() placeholder: string;
    @Input() searchPlaceholder: string = 'Search';

    emitSelect(newSelection: MatSelectChange) {
        this.valueChange.emit(newSelection.value);
        this.selectChange.emit(newSelection);
    }

    /**
     * For running a function when the selection was changed.
     */
    @Output()
    selectChange: EventEmitter<MatSelectChange> = new EventEmitter<MatSelectChange>();

    /**
     * For binding to the input value;
     */
    @Output()
    valueChange: EventEmitter<any> = new EventEmitter<any>();

    /*
    Please ensure that fuse works when using the string function.
     */

    _data: any[];

    get data(): any[] {
        return this._data;
    }

    @Input()
    set data(data: any[]) {
        this._data = data;
        this.refresh();
    }

    @Input()
    stringFunction: (obj: any, other?: any) => string = (obj) => {

        return obj.attributes?.description ?
            obj.attributes.name + ' - ' + obj.attributes.description :
            obj.attributes?.name;
    };

    @Input()
    set value(value: any) {
        if (value !== null) {
            this.selectFormControl.setValue(value);
        }
    }

    get disabled(): boolean {
        return this.selectFormControl.disabled;
    }

    @Input()
    set disabled(state: boolean) {
        if (state) {
            this.selectFormControl.disable();
        } else {
            this.selectFormControl.enable();
        }
    }

    @Input() fill_stubs: boolean = false;


    @Input()
    valueFunction: (obj: any) => any = (obj) => {
        return obj;
    }

    @Input()
    compareWith: (o1: any, o2: any) => boolean = (a, b) => {
        // lazy compare with, just to object comparison
        return a === b;
    }

    @Input() label: string = '';

    /**
     * Object keys used for search.
     */
    @Input() data_schema: any[] = [
        "attributes.name",
        "attributes.description",
    ];

    @Input() classes: string;
    description_map: { [key: string]: string } = {};
    value_map: { [key: string]: any } = {};

    search_options: any = {
        shouldSort: true,
        threshold: 0.65,
        location: 0,
        findAllMatches: true,
        isCaseSensitive: false,
        ignoreLocation: true,
        ignoreFieldNorm: true,
        distance: 100,
        maxPatternLength: 32,
        minMatchCharLength: 3,
        keys: []
    };
    private onDestroy: Subject<void> = new Subject();

    fuse: any;
    isOpen: boolean = false;

    constructor() {
    }

    refresh(): void {
        const ctrl = this;
        this.data.map(item => {
            this.description_map[item.id] = this.stringFunction(item);
            this.value_map[item.id] = this.valueFunction(item);
        });

        this.fuse = new Fuse(this.data, this.search_options);
        if (this.fill_stubs && ctrl.selectFormControl.value && ctrl.selectFormControl.value.id) {
            ctrl.data.forEach((item) => {
                if (item.id === ctrl.selectFormControl.value.id || item.attributes?.name === ctrl.selectFormControl.value) {
                    ctrl.selectFormControl.setValue(item);
                }
            });
        }
        this.filteredOptions = deepCopy(this.data);
    }

    ngOnInit(): void {
        this.search_options.keys = this.data_schema;
        if(!this.label){
            this.classes += ' no-label';
        }
        setTimeout(() => {
            this.refresh();

            /**
             * Set filter event based on value changes
             */
            this.searchTextboxControl.valueChanges
                .pipe(takeUntil(this.onDestroy),
                    debounceTime(300),
                    startWith<string>(''),
                    map(name => this._filter(name))
                ).subscribe(values => {
                this.filteredOptions = values;
            });
        });
    }

    ngOnDestroy(): void {
        this.onDestroy.next();
        this.onDestroy.unsubscribe();
    }

    /**
     * Used to filter data based on search input
     */
    private _filter(name: string = ''): string[] {
        const filterValue: string = name.toLowerCase();

        if (name === '') {
            return this.data;
        }

        // Set selected values to retain the selected checkbox state
        this.data.filter(option => this.description_map[option.id].toLowerCase().indexOf(filterValue) >= 0);
        return this.fuse.search(name)?.map(item => item.item);
    }

    openedChange(e: boolean) {
        this.isOpen = e;
        // Set search textbox value as empty while opening selectbox
        // Focus to search textbox while clicking on selectbox
        if (e) {
            this.searchTextboxControl.patchValue('');
            this.searchTextBox.nativeElement.focus();
        }
    }

    /**
     * Clearing search textbox value
     */
    clearSearch(event) {
        event.stopPropagation();
        this.placeholder = 'Search';
        this.searchTextboxControl.patchValue('');
    }

    /**
     * Set selected values to retain the state
     */

}

/**  Copyright 2018 Google Inc. All Rights Reserved.
 Use of this source code is governed by an MIT-style license that
 can be found in the LICENSE file at http://angular.io/license */
